import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import MenuContent from "./MenuContent";
import BusinessIcon from "@mui/icons-material/Business";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import logo from "../../Auth/Image/siddhart.png"; // Static fallback
import { blue } from "@mui/material/colors";
import { getCompanyProfile } from "../../Services/Services";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";

const drawerWidth = 220;
const collapsedWidth = 64;

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  transition: theme.transitions.create(["width", "background-color"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  }),

  [`& .${drawerClasses.paper}`]: {
    width: open ? drawerWidth : collapsedWidth,
    overflowX: "hidden",
    transition: theme.transitions.create(
      ["width 0.4s cubic-bezier(0.4, 0, 0.2, 1)", "background-color"],
      {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.standard,
      },
    ),
    borderRight: `1px solid ${theme.palette.divider}`,
  },
}));

// Styled Division Card Component (Unused but kept as original)
const DivisionCard = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}10 100%)`,
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.primary.main}30`,
  padding: theme.spacing(1.5),
  margin: theme.spacing(1),
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
    borderColor: theme.palette.primary.main,
  },
}));

export default function SideMenu() {
  const [open, setOpen] = useState(
  localStorage.getItem('sidebarOpen') !== 'false'
);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const navigate = useNavigate();

  // ----- ADDED: State for dynamic logo -----
  const [logoUrl, setLogoUrl] = useState(logo); // start with static fallback
  const [companyName, setCompanyName] = useState("");

  // ----- ADDED: Fetch company profile for logo -----
  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        const data = await getCompanyProfile();
        const profileData = Array.isArray(data) ? data[0] : data;
        if (profileData && profileData.logo_url) {
          setLogoUrl(profileData.logo_url);
          setCompanyName(profileData.company_name || "");
        }
      } catch (error) {
        console.error("Error fetching company profile for logo:", error);
        // Keep fallback logo
      }
    };
    fetchCompanyProfile();
  }, []);

  useEffect(() => {
    // Get division from localStorage
    const divisionData = localStorage.getItem("selectedDivision");
    if (divisionData) {
      try {
        setSelectedDivision(JSON.parse(divisionData));
      } catch (e) {
        console.error("Error parsing division data:", e);
      }
    }
  }, []);

const toggleDrawer = () => {
  const newState = !open;
  setOpen(newState);
  localStorage.setItem('sidebarOpen', newState);
  window.dispatchEvent(new Event('sidebarToggle')); // ✅ Notify AppNavbar
};
  const handleChangeDivision = () => {
    navigate("/division-card");
  };

  return (
    <>
      <StyledDrawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          width: open ? "200px" : "64px",
          transition: "width 0.8s ease",
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center",
            backgroundColor: "#fff",
            borderBottom: "1px solid",
            borderColor: "divider",
            px: open ? 1 : 0,
            py: 1,
             minHeight: "64px",   
    height: "64px",     
          }}
        >
          {/* ✅ Logo - Only show when open */}
          {open && (
            <Box
              component="img"
              src={logoUrl}
              alt={companyName || "Logo"}
              sx={{
                width: 140,
                height: "auto",
                display: "block",
                transition: "width 0.3s ease",
              }}
            />
          )}

          {/* ✅ Toggle Button - Always visible */}
          <Tooltip
            title={open ? "Collapse Menu" : "Expand Menu"}
            arrow
            placement="right"
          >
            <IconButton
              onClick={toggleDrawer}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "#f5f7fa",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "primary.lighter",
                  color: "primary.main",
                },
              }}
            >
              {open ? (
                <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
              ) : (
                <MenuRoundedIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <Divider />

        {/* Division Display Section */}
        {/* {selectedDivision && (
          <Box
            sx={{
              mx: 1,
              my: 1.5,
              p: open ? 1.5 : 1,
              borderRadius: 2,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "space-between" : "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              transition: "all 0.3s ease",
            }}
          >
            {open ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BusinessIcon sx={{ fontSize: 20 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  >
                    {selectedDivision.division}
                  </Typography>
                </Box>
                <Tooltip title="Change Division" arrow>
                  <IconButton
                    size="small"
                    onClick={() => navigate("/division-cards")}
                    sx={{
                      color: "white",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.3)",
                        transform: "rotate(180deg)",
                      },
                      transition: "all 0.3s ease",
                      width: 28,
                      height: 28,
                    }}
                  >
                    <SwapHorizIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip
                title={`${selectedDivision.division} - Click to change`}
                arrow
                placement="right"
              >
                <IconButton
                  size="small"
                  onClick={() => navigate("/division-card")}
                  sx={{
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )} */}

        {/* No Division Selected State */}
        {/* {!selectedDivision && (
          <>
            {open ? (
              <Box
                sx={{
                  mt: 3,
                  p: 1.5,
                  borderRadius: 2,
                  border: "2px dashed",
                  borderColor: "warning.main",
                  backgroundColor: "warning.lighter",
                  textAlign: "center",
                }}
              >
                <BusinessIcon
                  sx={{
                    fontSize: 28,
                    color: "warning.main",
                    mb: 0.5,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "warning.dark",
                    fontWeight: 500,
                    mb: 1,
                  }}
                >
                  No Division Selected
                </Typography>
                <Chip
                  label="Select Division"
                  size="small"
                  icon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                  onClick={handleChangeDivision}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: "warning.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "warning.dark",
                    },
                  }}
                />
              </Box>
            ) : (
              <Tooltip title="Select Division" arrow placement="right">
                <IconButton
                  onClick={handleChangeDivision}
                  sx={{
                    mx: "auto",
                    my: 1,
                    display: "flex",
                    width: 40,
                    height: 40,
                    backgroundColor: "warning.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "warning.dark",
                    },
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            <Divider />
          </>
        )} */}

          <MenuContent />

      </StyledDrawer>
    </>
  );
}
